import React, { useEffect, useState } from 'react';
import { useQuizEditor, QuizEditorFormState } from '@/contexts/QuizEditorContext';
import { useBrandSelection } from '@/hooks/useBrandSelection';
import { useQuiz, useCreateQuiz, useUpdateQuiz } from '@/hooks/useQuizzes';
import { useQuizTemplates, useCreateQuizTemplate } from '@/hooks/useQuizTemplates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AiTextarea } from '@/components/ui/ai-textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Drawer,
  DrawerContent,
} from '@/components/ui/drawer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { 
  X, 
  Save,
  FileText,
  Import,
  Loader2,
} from 'lucide-react';
import { QuestionBuilder } from './QuestionBuilder';
import { QuizActionManager } from './QuizActionManager';
import { ScoringConfigEditor } from './ScoringConfigEditor';
import { TemplateSelector } from './TemplateSelector';
import { SelectQuizSourceDialog } from './SelectQuizSourceDialog';
import type { QuizTemplate } from '@/types/quiz';
import { toast } from 'sonner';

export function GlobalQuizEditor() {
  const {
    isOpen,
    quizId,
    isNew,
    formState,
    activeTab,
    setFormState,
    setActiveTab,
    closeEditor,
    resetEditor,
    updateFormField,
  } = useQuizEditor();

  const { currentBrandId } = useBrandSelection();
  const { data: existingQuiz, isLoading: isLoadingQuiz } = useQuiz(isNew ? undefined : quizId || undefined);
  const { data: templates } = useQuizTemplates(currentBrandId);
  const createQuiz = useCreateQuiz();
  const updateQuiz = useUpdateQuiz();
  const createTemplate = useCreateQuizTemplate();

  const [isSaving, setIsSaving] = useState(false);
  const [hasLoadedQuiz, setHasLoadedQuiz] = useState<string | null>(null);
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);

  // Load existing quiz data when editing
  useEffect(() => {
    if (existingQuiz && quizId && hasLoadedQuiz !== quizId) {
      setFormState({
        name: existingQuiz.name,
        description: existingQuiz.description || '',
        instructions: existingQuiz.instructions || '',
        template_id: existingQuiz.template_id || '',
        stepping_approach: existingQuiz.stepping_approach as any,
        feedback_mode: existingQuiz.feedback_mode as any,
        explanation_timing: existingQuiz.explanation_timing as any,
        questions: Array.isArray(existingQuiz.questions) ? existingQuiz.questions as any : [],
        actions: Array.isArray(existingQuiz.actions_after_submit) ? existingQuiz.actions_after_submit as any : [],
        estimated_seconds_per_question: existingQuiz.estimated_seconds_per_question || 120,
        scoring_config: (existingQuiz as any).scoring_config || { mode: 'percentage', score_ranges: [] },
      });
      setHasLoadedQuiz(quizId);
    }
  }, [existingQuiz, quizId, hasLoadedQuiz, setFormState]);

  // Reset loaded quiz tracker when quizId changes to a new quiz
  useEffect(() => {
    if (isNew) {
      setHasLoadedQuiz(null);
    }
  }, [isNew]);

  const handleSelectTemplate = (template: QuizTemplate) => {
    setFormState({
      name: `${template.name} Quiz`,
      description: template.description || '',
      instructions: template.instructions || '',
      template_id: template.id,
      stepping_approach: template.stepping_approach as any,
      feedback_mode: template.feedback_mode as any,
      explanation_timing: template.explanation_timing as any,
      questions: Array.isArray(template.questions) ? template.questions as any : [],
      actions: Array.isArray(template.actions_after_submit) ? template.actions_after_submit as any : [],
      estimated_seconds_per_question: 120,
      scoring_config: (template as any).scoring_config || { mode: 'percentage', score_ranges: [] },
    });
    toast.success('Template loaded successfully');
  };

  const handleSaveAsTemplate = async () => {
    if (!currentBrandId) {
      toast.error('Please select a brand first');
      return;
    }

    const templateName = prompt('Enter a name for this template:');
    if (!templateName) return;

    const selectedTemplate = formState.template_id ? templates?.find(t => t.id === formState.template_id) : null;
    
    await createTemplate.mutateAsync({
      brand_id: currentBrandId,
      user_id: '',
      name: templateName,
      description: formState.description,
      quiz_type_id: selectedTemplate?.quiz_type_id || null,
      quiz_style_id: selectedTemplate?.quiz_style_id || null,
      is_global: false,
      questions: formState.questions as any,
      stepping_approach: formState.stepping_approach,
      feedback_mode: formState.feedback_mode,
      explanation_timing: formState.explanation_timing,
      visual_config: {},
      actions_after_submit: formState.actions as any,
      instructions: formState.instructions,
      category: null,
      thumbnail_url: null,
    });
    toast.success('Template saved');
  };

  const handleSave = async () => {
    if (!currentBrandId) {
      toast.error('Please select a brand');
      return;
    }
    
    if (!formState.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!formState.template_id) {
      toast.error('Template is required');
      return;
    }

    setIsSaving(true);

    const selectedTemplate = formState.template_id ? templates?.find(t => t.id === formState.template_id) : null;

    const quizData = {
      name: formState.name,
      description: formState.description,
      instructions: formState.instructions,
      template_id: formState.template_id,
      stepping_approach: formState.stepping_approach,
      feedback_mode: formState.feedback_mode,
      explanation_timing: formState.explanation_timing,
      brand_id: currentBrandId,
      quiz_type_id: selectedTemplate?.quiz_type_id || null,
      quiz_style_id: selectedTemplate?.quiz_style_id || null,
      questions: formState.questions as any,
      actions_after_submit: formState.actions as any,
      estimated_seconds_per_question: formState.estimated_seconds_per_question,
      scoring_config: formState.scoring_config as any,
    };

    try {
      if (isNew) {
        await createQuiz.mutateAsync(quizData as any);
        toast.success('Quiz created');
        resetEditor();
        closeEditor();
      } else {
        await updateQuiz.mutateAsync({
          id: quizId!,
          updates: quizData,
        });
        toast.success('Quiz saved');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={(open) => !open && closeEditor()} direction="right">
        <DrawerContent className="h-full w-full max-w-none inset-y-0 right-0 left-auto rounded-none mt-0 fixed">
          {!currentBrandId ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">Please select a brand to create quizzes.</p>
            </div>
          ) : !isNew && isLoadingQuiz ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b bg-background">
                <div className="flex items-center gap-4">
                  <Button variant="ghost" size="icon" onClick={closeEditor}>
                    <X className="h-4 w-4" />
                  </Button>
                  <div>
                    <h1 className="text-lg font-semibold">{isNew ? 'New Quiz' : 'Edit Quiz'}</h1>
                    <p className="text-sm text-muted-foreground">{formState.name || 'Untitled'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isNew && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTemplateSelectorOpen(true)}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Load Template
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveAsTemplate}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save as Template
                  </Button>
                  <Button onClick={handleSave} disabled={isSaving}>
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    {isNew ? 'Create' : 'Save'}
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                  <div className="px-6 pt-4 border-b">
                    <TabsList>
                      <TabsTrigger value="details">Details</TabsTrigger>
                      <TabsTrigger value="questions">Questions</TabsTrigger>
                      <TabsTrigger value="scoring">Scoring</TabsTrigger>
                      <TabsTrigger value="actions">Actions</TabsTrigger>
                    </TabsList>
                  </div>

                  <ScrollArea className="flex-1">
                    <div className="p-6">
                      <TabsContent value="details" className="mt-0 space-y-6">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="name">Quiz Name *</Label>
                            <Input
                              id="name"
                              placeholder="My Awesome Quiz"
                              value={formState.name}
                              onChange={(e) => updateFormField('name', e.target.value)}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="description">Description</Label>
                            <AiTextarea
                              id="description"
                              placeholder="What is this quiz about?"
                              value={formState.description}
                              onChange={(e) => updateFormField('description', e.target.value)}
                              fieldContext={{
                                fieldName: 'quiz_description',
                                pageContext: 'Quiz Builder',
                                brandId: currentBrandId,
                                additionalContext: `Quiz name: ${formState.name || 'Untitled Quiz'}`,
                              }}
                              onSuggestionAccepted={(suggestion) => updateFormField('description', suggestion)}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="instructions">Instructions</Label>
                            <AiTextarea
                              id="instructions"
                              placeholder="Instructions for quiz takers"
                              value={formState.instructions}
                              onChange={(e) => updateFormField('instructions', e.target.value)}
                              fieldContext={{
                                fieldName: 'quiz_instructions',
                                pageContext: 'Quiz Builder',
                                brandId: currentBrandId,
                                additionalContext: `Quiz name: ${formState.name || 'Untitled Quiz'}. Description: ${formState.description || 'No description'}`,
                              }}
                              onSuggestionAccepted={(suggestion) => updateFormField('instructions', suggestion)}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Template *</Label>
                            <Select 
                              value={formState.template_id} 
                              onValueChange={(value) => updateFormField('template_id', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select a template" />
                              </SelectTrigger>
                              <SelectContent>
                                {templates?.map((template) => (
                                  <SelectItem key={template.id} value={template.id}>
                                    {template.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Stepping Approach</Label>
                              <Select 
                                value={formState.stepping_approach} 
                                onValueChange={(value: any) => updateFormField('stepping_approach', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="single-step">Single Step</SelectItem>
                                  <SelectItem value="multiple-steps">Multiple Steps</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2">
                              <Label>Feedback Mode</Label>
                              <Select 
                                value={formState.feedback_mode} 
                                onValueChange={(value: any) => updateFormField('feedback_mode', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="immediate">Immediate</SelectItem>
                                  <SelectItem value="time-delay">Time Delay</SelectItem>
                                  <SelectItem value="on-submit">On Submit</SelectItem>
                                  <SelectItem value="after-completion">After Completion</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2">
                              <Label>Explanation Timing</Label>
                              <Select 
                                value={formState.explanation_timing} 
                                onValueChange={(value: any) => updateFormField('explanation_timing', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="immediate">Immediate</SelectItem>
                                  <SelectItem value="on-submit">On Submit</SelectItem>
                                  <SelectItem value="results-only">Results Only</SelectItem>
                                  <SelectItem value="never">Never</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2">
                              <Label>Estimated Seconds per Question</Label>
                              <Input
                                type="number"
                                min={10}
                                max={600}
                                value={formState.estimated_seconds_per_question}
                                onChange={(e) => updateFormField('estimated_seconds_per_question', parseInt(e.target.value) || 120)}
                              />
                              <p className="text-xs text-muted-foreground">
                                Used to show estimated time on splash screen. Default: 120 seconds (2 min).
                              </p>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="questions" className="mt-0">
                        <Card>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <CardTitle>Questions</CardTitle>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSourceDialogOpen(true)}
                              >
                                <Import className="h-4 w-4 mr-2" />
                                Add from Source
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <QuestionBuilder 
                              questions={formState.questions} 
                              onChange={(questions) => updateFormField('questions', questions)} 
                            />
                          </CardContent>
                        </Card>
                      </TabsContent>

                      <TabsContent value="scoring" className="mt-0">
                        <Card>
                          <CardHeader>
                            <CardTitle>Scoring Configuration</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <ScoringConfigEditor
                              config={formState.scoring_config}
                              onChange={(config) => updateFormField('scoring_config', config)}
                            />
                          </CardContent>
                        </Card>
                      </TabsContent>

                      <TabsContent value="actions" className="mt-0">
                        <Card>
                          <CardContent className="pt-6">
                            <QuizActionManager 
                              actions={formState.actions} 
                              onChange={(actions) => updateFormField('actions', actions)} 
                            />
                          </CardContent>
                        </Card>
                      </TabsContent>
                    </div>
                  </ScrollArea>
                </Tabs>
              </div>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      <TemplateSelector
        open={templateSelectorOpen}
        onOpenChange={setTemplateSelectorOpen}
        onSelectTemplate={handleSelectTemplate}
        onStartFromScratch={() => setTemplateSelectorOpen(false)}
        brandId={currentBrandId}
      />

      <SelectQuizSourceDialog
        brandId={currentBrandId}
        open={sourceDialogOpen}
        onOpenChange={setSourceDialogOpen}
        onImport={(importedQuestions) => updateFormField('questions', [...formState.questions, ...importedQuestions])}
      />
    </>
  );
}
